import { useTranslator } from '@Localization';
import { useConfiguration } from 'common/hooks/useConfiguration';
import { observer } from 'mobx-react-lite';
import { FormEvent, ReactElement } from 'react';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { S_Link } from '../../../../design/links/S_Link';
import { ColumnLayout } from '../../../../modules/common/layouts/columnLayout/ColumnLayout';
import { FormAlert } from '../../../components/FormAlert';
import { ValidatedTextInput } from '../../../components/ValidatedTextInput';
import { InputStatus } from '../../../models/error.model';
import { Auth } from '../../../routes/config/Auth';

interface IProps {
  emailStatus: InputStatus;
  passwordStatus: InputStatus;
  externalStatus: InputStatus;
  email: string;
  isLoginInProgress: boolean;
  lockoutTime: number;
  handleEmailChange: (value: string) => void;
  handlePasswordChange: (value: string) => void;
  validateEmail: (value: string) => void;
  validatePassword: (value: string) => void;
  handleLogin: () => Promise<void>;
}

export const LoginForm = observer((props: IProps): ReactElement => {
  const { t } = useTranslator();
  const config = useConfiguration();

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    void props.handleLogin();
  };

  const loginLocked = props.emailStatus.isValid === false || props.passwordStatus.isValid === false || props.isLoginInProgress || props.lockoutTime > 0;

  return (
    <S_Card>
      <ColumnLayout size='l'>
        {props.lockoutTime === 0 && (
          <FormAlert
            id='login-error'
            errors={props.externalStatus}
            useDefaultMessageWhenCodeNotFound={true}
          />
        )}
        {props.lockoutTime > 0 && <S_Alert color='warning'>{t('account_locked', { timer: props.lockoutTime })}</S_Alert>}
        <div className='form-heder'>{t('sign_in')}</div>
        <form
          onSubmit={onSubmit}
          className='auth-form'
        >
          <ValidatedTextInput
            isRequired
            labelText={t('email_address')}
            type={'text'}
            id='login-email-input'
            value={props.email}
            placeholder={t('enter_email')}
            onChange={props.handleEmailChange}
            onBlur={props.validateEmail}
            isValid={props.emailStatus.isValid}
            feedbacks={props.emailStatus.errorCodes}
          />
          <ValidatedTextInput
            isRequired
            labelText={t('password')}
            type={'password'}
            id='login-password-input'
            placeholder={t('enter_password')}
            onChange={props.handlePasswordChange}
            onBlur={props.validatePassword}
            isValid={props.passwordStatus.isValid}
            feedbacks={props.passwordStatus.errorCodes}
          />
          <ButtonWithLoader
            id='login-button'
            loaderKey='login-button'
            type='submit'
            color='primary'
            disabled={loginLocked}
            onClick={onSubmit}
            loading={props.isLoginInProgress}
          >
            {t('login')}
          </ButtonWithLoader>
        </form>
        {config.appConfig.registrationEnabled && (
          <ColumnLayout size='md'>
            <div className='auth-link'>
              {t('forgot_password')}
              <S_Link
                color='anchor'
                to={Auth.ForgotPassword.Path}
              >
                {t('forgot_password_with_link')}
              </S_Link>
            </div>
            <div className='auth-link'>
              {t('no_account')}
              <S_Link
                color='anchor'
                to={Auth.Register.Path}
              >
                {t('no_account_with_link')}
              </S_Link>
            </div>
          </ColumnLayout>
        )}
      </ColumnLayout>
    </S_Card>
  );
});
